import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ResponseObjectModel } from 'src/app/domain/models/response-object.model';
import {
  BankAccountDto,
  SendAccountReceiverDto,
} from 'src/app/domain/models/dto/bank-account.dto';
import { CorporateAccountDto } from 'src/app/domain/models/dto/corporate-account.dto';
import { PurposeStatementDto } from 'src/app/domain/models/dto/purpose-statement.dto';
import { SocioEnvironmentalTermsDto } from 'src/app/domain/models/dto/socio-environmental-terms.dto';
import { environment } from 'src/environments/environment';
import { AuthenticationService } from '.';
import { ClientsDto } from 'src/app/domain/models/dto/clients.dto';
import { GetAllCnpjDto } from 'src/app/domain/models/dto/get-all-cnpj.dto';
import { FilterClientsDto } from 'src/app/domain/models/dto/filter-clients.dto';
import { CompanyTaxesDto } from 'src/app/domain/models/dto/company-taxes.dto';
import { BusinessPartnerDto } from 'src/app/domain/models/dto/business-partner.dto';
import { PartnerDto } from 'src/app/domain/models/dto/partner.dto';
import { ResponseTableModel } from 'src/app/domain/models/response-table.model';
import { HistoryDto } from 'src/app/domain/models/dto/history.dto';

@Injectable({
  providedIn: 'root',
})
export class CorporateAccountService {
  private partnerListener: BehaviorSubject<boolean> = new BehaviorSubject(null);
  private clientLogListener: BehaviorSubject<boolean> = new BehaviorSubject(
    null
  );

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthenticationService
  ) { }

  register(CorporateAccountDto: CorporateAccountDto) {
    return this.http.post<CorporateAccountDto>(
      `${environment.API_BASE_URL}/v1/corporate-accounts`,
      CorporateAccountDto
    );
  }

  putRegister(
    CorporateAccountDto: CorporateAccountDto,
    CorporateAccountDtoId: number
  ) {
    return this.http.put<ResponseObjectModel<CorporateAccountDto>>(
      `${environment.API_BASE_URL}/v1/corporate-accounts/${CorporateAccountDtoId}`,
      CorporateAccountDto
    );
  }

  public sendAccountReceiver(
    account: SendAccountReceiverDto,
    companyId: number
  ) {
    return this.http.put<ResponseObjectModel<SendAccountReceiverDto>>(
      `${environment.API_BASE_URL}/v1/corporate-accounts/${companyId}/account-banks`,
      account
    );
  }

  validateMembers(members) {
    return this.http.put<ResponseObjectModel<CorporateAccountDto>>(
      `${environment.API_BASE_URL}/v1/corporate-accounts/members/validate`,
      members
    );
  }

  updateMembers(members) {
    return this.http.put<ResponseObjectModel<CorporateAccountDto>>(
      `${environment.API_BASE_URL}/v1/corporate-accounts/members`,
      members
    );
  }

  public uploadSocialContract(id: number, file: any) {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.put<CorporateAccountDto>(
      `${environment.API_BASE_URL}/v1/corporate-accounts/${id}/social-contract`,
      formData
    );
  }

  public uploadResidenceFile(id: number, file: any) {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.put<CorporateAccountDto>(
      `${environment.API_BASE_URL}/v1/corporate-accounts/${id}/residential-file`,
      formData
    );
  }

  public uploadBalanceFile(id: number, file: any) {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.put<CorporateAccountDto>(
      `${environment.API_BASE_URL}/v1/corporate-accounts/${id}/balance-revenue`,
      formData
    );
  }

  get(): Observable<ResponseObjectModel<CorporateAccountDto>> {
    const url = `${environment.API_BASE_URL
      }/v1/corporate-accounts/${this.authService.getCompanyId()}`;
    return this.http.get<ResponseObjectModel<CorporateAccountDto>>(url);
  }

  getAllCnpj(): Observable<ResponseObjectModel<GetAllCnpjDto[]>> {
    const url = `${environment.API_BASE_URL}/v1/corporate-accounts/admin/getall-cnpj`;
    return this.http.get<ResponseObjectModel<GetAllCnpjDto[]>>(url);
  }

  getDetails(
    corporateAccountId: number
  ): Observable<ResponseObjectModel<CorporateAccountDto>> {
    const url = `${environment.API_BASE_URL}/v1/corporate-accounts/${corporateAccountId}`;
    return this.http.get<ResponseObjectModel<CorporateAccountDto>>(url);
  }

  updateSocioEnvironmentalTerms(
    CorporateAccountDtoId: number,
    data: SocioEnvironmentalTermsDto
  ) {
    return this.http.put<CorporateAccountDto>(
      `${environment.API_BASE_URL}/v1/corporate-accounts/${CorporateAccountDtoId}/socioenvironmental-terms`,
      data
    );
  }

  setClientConfig(config: CompanyTaxesDto) {
    return this.http.put<CompanyTaxesDto>(
      `${environment.API_BASE_URL}/v1/corporate-accounts/staff/set-company-taxes`,
      config
    );
  }

  updateBankAccount(CorporateAccountDtoId: number, data: BankAccountDto) {
    return this.http.put<CorporateAccountDto>(
      `${environment.API_BASE_URL}/v1/corporate-accounts/${CorporateAccountDtoId}/bank-accounts`,
      data
    );
  }

  updatePurposeStatement(
    CorporateAccountDtoId: number,
    data: PurposeStatementDto
  ) {
    return this.http.put<CorporateAccountDto>(
      `${environment.API_BASE_URL}/v1/corporate-accounts/${CorporateAccountDtoId}/purpose-statements`,
      data
    );
  }

  getPartnersList(): Observable<ResponseObjectModel<BusinessPartnerDto[]>> {
    const url = `${environment.API_BASE_URL}/v1/business-partners/get-all`;
    return this.http.get<ResponseObjectModel<BusinessPartnerDto[]>>(url);
  }

  uploadPartnerFile(payload: {
    partnerId: number | string;
    file: File;
  }): Observable<string> {
    const url = `${environment.API_BASE_URL}/v1/partner/clients/import-file/${payload.partnerId}`;

    const form = new FormData();
    form.append('partnerId', String(payload.partnerId));
    form.append('file', payload.file);

    return this.http.post(url, form, { responseType: 'text' });
  }

  getPartnersTable(
    pageNumber?: number,
    pageSize?: number
  ): Observable<ResponseObjectModel<PartnerDto[]>> {
    const url = `${environment.API_BASE_URL}/v1/business-partners?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    return this.http.get<ResponseObjectModel<PartnerDto[]>>(url);
  }

  createPartner(partner: PartnerDto) {
    return this.http.post<PartnerDto>(
      `${environment.API_BASE_URL}/v1/business-partners`,
      partner
    );
  }

  editPartner(config: PartnerDto) {
    return this.http.put<PartnerDto>(
      `${environment.API_BASE_URL}/v1/business-partners/${config?.id}/rates`,
      config
    );
  }

  get listenerUpdate() {
    return this.partnerListener.asObservable();
  }

  get listenerClientUpdate() {
    return this.clientLogListener.asObservable();
  }

  updatePartnerView() {
    this.partnerListener.next(true);
  }

  getAccountStatus(
    filter: FilterClientsDto,
    pageNumber?: number,
    pageSize?: number
  ): Observable<ResponseObjectModel<ResponseTableModel<ClientsDto[]>>> {
    const params: string[] = [];

    if (pageNumber !== undefined) {
      params.push(`pageNumber=${pageNumber}`);
    }
    if (pageSize !== undefined) {
      params.push(`pageSize=${pageSize}`);
    }
    if (filter.personType) {
      params.push(`personType=${encodeURIComponent(filter.personType)}`);
    }

    const queryString = params.length > 0 ? `?${params.join('&')}` : '';
    const url = `${environment.API_BASE_URL}/v1/account-status/accounts${queryString}`;
    return this.http.get<ResponseObjectModel<ResponseTableModel<ClientsDto[]>>>(
      url
    );
  }

  getAccountStatusOverview(): Observable<
    ResponseObjectModel<{
      nextToExpire: number;
      expired: number;
      pendingDocument: number;
      noLimit: number;
    }>
  > {
    const url = `${environment.API_BASE_URL}/v1/account-status/overview`;
    return this.http.get<
      ResponseObjectModel<{
        nextToExpire: number;
        expired: number;
        pendingDocument: number;
        noLimit: number;
      }>
    >(url);
  }

  getAccountStatusDetail(
    clientId: number,
    personType: string
  ): Observable<ResponseObjectModel<ClientsDto>> {
    const params: string[] = [];

    if (clientId !== undefined) {
      params.push(`clientId=${clientId}`);
    }
    if (personType) {
      params.push(`personType=${encodeURIComponent(personType)}`);
    }

    const queryString = params.length > 0 ? `?${params.join('&')}` : '';
    const url = `${environment.API_BASE_URL}/v1/account-status/accounts/detail${queryString}`;
    return this.http.get<ResponseObjectModel<ClientsDto>>(url);
  }

  getCachedAccountStatusOverview(): Observable<
    ResponseObjectModel<{
      nextToExpire: number;
      expired: number;
      pendingDocument: number;
      noLimit: number;
    }>
  > {
    const url = `${environment.API_BASE_URL}/v1/account-status/overview-cached`;
    return this.http.get<
      ResponseObjectModel<{
        nextToExpire: number;
        expired: number;
        pendingDocument: number;
        noLimit: number;
      }>
    >(url);
  }

  getCorporateAccountHistory(
    clientId: number,
    personType: string,
    pageNumber?: number,
    pageSize?: number
  ): Observable<ResponseObjectModel<ResponseTableModel<HistoryDto[]>>> {
    const params: string[] = [];

    if (clientId !== undefined) {
      params.push(`identifier=${clientId}`);
    }

    if (personType !== undefined) {
      params.push(`entity=${personType}`);
    }

    if (pageNumber !== undefined) {
      params.push(`pageNumber=${pageNumber}`);
    }
    if (pageSize !== undefined) {
      params.push(`pageSize=${pageSize}`);
    }

    const queryString = params.length > 0 ? `?${params.join('&')}` : '';
    const url = `${environment.API_BASE_URL}/v1/histories${queryString}`;
    return this.http.get<ResponseObjectModel<ResponseTableModel<HistoryDto[]>>>(
      url
    );
  }

  updateClientLogView() {
    this.clientLogListener.next(true);
  }
}
